import { exists } from '@jack-henry/frontend-utils/functions';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import {
    ChallengeType,
    ErrorType,
    StatusType,
    type SecurityMessage,
} from '@treasury/domain/channel/mappings/security';
import {
    coerceSecurityMessage,
    extractSecurityPayload,
    isLockedOrSuccessful,
    TmApiError,
} from '@treasury/domain/shared';
import type { IdentityDialogOtpRequest } from '@treasury/domain/shared/fetch-with-auth';
import { LitPropertyVerifier } from '@treasury/utils/lit-helpers';
import { html, nothing, PropertyValueMap, TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import {
    IdentityDialogContext,
    IdentityDialogResultInternal,
    IdentityDialogStrategy,
} from './identity-verification-dialog.types';
// avoid import from barrel file to ensure modern components aren't brought in
import { NullIdentityStrategy } from './strategies/null-strategy';

/**
 * Base class for identity verification dialog components.
 *
 * Primarily exists to share logic between modern and legacy implementations.
 *
 * TODO: Refactor into main implementation when the Omega one is removed.
 */
export abstract class IvdBase extends LitPropertyVerifier implements IdentityDialogContext {
    @property({
        type: Object,
    })
    public securityMessage!: SecurityMessage;

    @property({
        type: Boolean,
    })
    public forLogin = false;

    /**
     * Perform an MFA request without any user interaction.
     * If this is `true`, no UI is presented to the user.
     */
    @property({
        type: Boolean,
    })
    public withoutUserInteraction = false;

    @property({
        type: Boolean,
    })
    public disableClose = false;

    /**
     * Function used to make an API request with password from this dialog.
     * Provided by fetch code.
     */
    @property({
        type: Function,
    })
    public otpRequestFn?: IdentityDialogOtpRequest;

    protected verifiedPropNames: (keyof this)[] = ['securityMessage'];

    @state()
    private _verifying = false;

    public get password() {
        const { password } = this.secureTokenStrategy;
        if (exists(password) && password.length > 1) {
            this._password = password;
        }

        return this._password || '';
    }

    public set password(value) {
        this.secureTokenStrategy.password = this._password = value;
    }

    public get verifying() {
        return this._verifying;
    }

    /**
     * The last entered password stored separately from the token strategy.
     *
     * This enables setting the password even if the strategy doesn't support it,
     * which can be useful in test scenarios.
     */
    private _password?: string;

    @state()
    private secureTokenStrategy = NullIdentityStrategy;

    private readonly verificationStream = new ObservationSource<SecurityMessage>();

    private get errorMessage() {
        const { errorCode, message } = this.securityMessage;
        if (!errorCode || !message || errorCode === ErrorType.Verify) {
            return undefined;
        }

        return message;
    }

    /**
     * The UI is waiting for user interaction.
     */
    private get waitingForUser() {
        return !this.verifying && this.securityMessage.status !== StatusType.Locked;
    }

    private get isSuccessful() {
        return this.securityMessage.status === StatusType.Success;
    }

    private set isSuccessful(val) {
        if (val) {
            this.securityMessage.status = StatusType.Success;
        } else {
            this.securityMessage.status = StatusType.Failure;
            this.securityMessage.message = 'Verification was unsuccessful.';
        }

        this.requestUpdate('isSuccessful');
    }

    /**
     * Promise that completes the first time the dialog is opened
     * and automatic OOB verification has occurred.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly automaticVerification = this.verificationStream.toObservable().toPromise(true);

    public render() {
        if (this.isSuccessful) {
            return html`<div class="success-message">You authentication was successful</div>`;
        }

        if (this.verifying) {
            return this.renderProgress();
        }

        return html`<div class="container">
            ${this.renderError(this.errorMessage)} ${this.renderContent()} ${this.renderActions()}
        </div> `;
    }

    public cancel() {
        this._verifying = false;
        this.password = '';
        this.close(DynamicComponentExitReason.Cancel, {
            message: this.securityMessage,
        });
    }

    /**
     * Verify user identity using the challenge type and provided credentials (if any).
     * @returns `true` if the verification was successful.
     */
    public async verify() {
        if (!exists(this.password)) {
            return false;
        }

        if (this.forLogin) {
            if (await this.verifyAccountPassword(this.password)) {
                this.close(DynamicComponentExitReason.Confirm, {
                    message: this.securityMessage,
                });
                return true;
            }
            return false;
        }

        // eslint-disable-next-line default-case
        switch (this.securityMessage.challengeMethodTypeId) {
            case ChallengeType.SecureToken:
            case ChallengeType.OutOfBandOTP: {
                this.securityMessage.oneTimePassword = this.password;
                return this.performPasswordRequest();
            }
            case ChallengeType.OutOfBand: {
                if (!this.otpRequestFn) {
                    throw new Error(
                        'Could not validate identity using OOB. No request function provided.'
                    );
                }

                const response = await this.otpRequestFn(this.securityMessage);
                const securityPayload = await extractSecurityPayload(response);

                if (!securityPayload) {
                    throw new Error(
                        'Could not validate identity using OOB. Response contained no security payload.'
                    );
                }

                this.securityMessage = coerceSecurityMessage(securityPayload);

                if (isLockedOrSuccessful(this.securityMessage)) {
                    this.close(DynamicComponentExitReason.Confirm, {
                        response,
                        message: this.securityMessage,
                    });

                    return true;
                }

                return false;
            }
        }

        return false;
    }

    /**
     * Inform consumers that the original request that results in an OTP
     * code being sent to the user should be invoked again.
     */
    public async reverify(securityMessage: SecurityMessage) {
        if (!this.otpRequestFn) {
            throw new Error('Could not re-verify. No request function provided.');
        }

        this._verifying = true;
        const response = await this.otpRequestFn({
            ...this.securityMessage,
            ...securityMessage,
        });
        const securityPayload = await extractSecurityPayload(response);
        if (!securityPayload) {
            throw new Error('Could not re-verify. No security payload in response');
        }

        this.securityMessage = coerceSecurityMessage(securityPayload);
    }

    protected abstract renderError(errorMessage?: string): TemplateResult | typeof nothing;

    protected abstract renderProgress(): TemplateResult | typeof nothing;

    protected abstract close(
        reason: DynamicComponentExitReason,
        result: IdentityDialogResultInternal
    ): void;

    protected abstract createDialogStrategy(
        challengeMethodType: SecurityMessage['challengeMethodTypeId']
    ): IdentityDialogStrategy;

    protected firstUpdated(changedProps: PropertyValueMap<typeof this>) {
        super.firstUpdated(changedProps);
        this.onPropsChange(changedProps);

        if (this.withoutUserInteraction) {
            this.verifyViaOob();
        }
    }

    protected updated(changedProps: PropertyValueMap<typeof this>) {
        super.updated(changedProps);
        this.onPropsChange(changedProps);
    }

    private onPropsChange(changedProps: PropertyValueMap<typeof this>) {
        if (
            changedProps.has('securityMessage') &&
            exists(this.securityMessage.challengeMethodTypeId)
        ) {
            this.secureTokenStrategy = this.createDialogStrategy(
                this.securityMessage.challengeMethodTypeId
            );
        }
    }

    private renderContent() {
        if (!this.waitingForUser) {
            return nothing;
        }

        return this.secureTokenStrategy.renderContent();
    }

    private renderActions() {
        if (!this.waitingForUser) {
            return nothing;
        }

        return this.secureTokenStrategy.renderActions();
    }

    private async verifyAccountPassword(password: string) {
        if (password.length === 0) {
            return false;
        }

        this._verifying = true;

        const { success, result } =
            await this.secureTokenStrategy.validateAccountPassword(password);

        if (!success) {
            const challengeMethodTypeId =
                result?.challengeMethodTypeId ?? this.securityMessage.challengeMethodTypeId;

            this.securityMessage = {
                ...this.securityMessage,
                ...result,
                challengeMethodTypeId,
                errorCode: ErrorType.Failure,
                message: result?.message ?? 'Verification was unsuccessful.',
            };

            this.password = '';
        }

        this.isSuccessful = success;
        this._verifying = false;
        return success;
    }

    /**
     * Make a request using the user-provided OTP.
     */
    private async performPasswordRequest() {
        if (!this.otpRequestFn) {
            throw new Error(
                'Cannot perform OTP request. No request function provided to identity dialog.'
            );
        }

        this._verifying = true;
        try {
            const response = await this.otpRequestFn({
                ...this.securityMessage,
                oneTimePassword: this.password,
            });

            const securityPayload = await extractSecurityPayload(response);
            if (!securityPayload) {
                this.isSuccessful = false;
                return false;
            }

            const securityMessage = coerceSecurityMessage(securityPayload);
            const { status, errorCode } = securityMessage;
            this.password = '';
            this._verifying = false;

            if (status !== StatusType.Success && errorCode !== ErrorType.Locked) {
                // API  zeroes out failures; preserve value from original message
                if (securityMessage.challengeMethodTypeId === 0) {
                    securityMessage.challengeMethodTypeId =
                        this.securityMessage.challengeMethodTypeId;
                }

                this.isSuccessful = false;
                this.securityMessage = securityMessage;
                return false;
            }

            this.isSuccessful = true;
            this.close(DynamicComponentExitReason.Confirm, {
                message: securityMessage,
                response,
            });

            return true;
        } catch (e) {
            this.isSuccessful = false;
            const data =
                e instanceof TmApiError
                    ? e
                    : {
                          message: {
                              challengeMethodTypeId: 0,
                          },
                      };
            this.close(DynamicComponentExitReason.ForceClose, data);
            return false;
        }
    }

    private async verifyViaOob() {
        if (!this.otpRequestFn) {
            throw new Error(
                'Cannot perform request-without-user-interaction. No request function provided.'
            );
        }

        this._verifying = true;

        const response = await this.otpRequestFn(this.securityMessage);
        const securityPayload = await extractSecurityPayload(response);

        if (!securityPayload) {
            throw new Error('Could extract a security payload from the response.');
        }

        const securityMessage = coerceSecurityMessage(securityPayload);
        this.securityMessage = securityMessage;

        if (isLockedOrSuccessful(this.securityMessage)) {
            this.close(DynamicComponentExitReason.Confirm, {
                message: this.securityMessage,
                response,
            });
        }

        this._verifying = false;
        this.verificationStream.emit(securityMessage);
        this.verificationStream.complete();
    }
}
